import { useState, useEffect, useCallback } from "react";
import { useNotifications } from "../contexts/NotificationContext";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../services/api";

type Achievement = {
	id: string;
	title: string;
	icon: string;
	description: string;
	secret: boolean;
	unlocked: boolean;
	notified: boolean;
};

const AchievementsPanel = ({ ultraMode = false }) => {
	const { addNotification } = useNotifications();
	const [hoveredAchievement, setHoveredAchievement] = useState<string | null>(null);
	const [achievements, setAchievements] = useState<Achievement[]>([]);
	const [loading, setLoading] = useState(true);

	const fetchAchievements = useCallback(async () => {
		try {
			const data = await api.getAchievements();
			setAchievements(data);
		} catch (error) {
			console.error("Error fetching achievements:", error);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchAchievements();
	}, [fetchAchievements]);

	// Notifica conquistas em um useEffect separado
	useEffect(() => {
		const unlockedAchievements = achievements.filter((a) => a.unlocked);
		unlockedAchievements.forEach((achievement) => {
			if (!achievement.notified) {
				addNotification({
					message: `Achievement Unlocked: ${achievement.title}`,
					type: "success",
					icon: achievement.icon,
				});
				// Marca como notificado localmente
				setAchievements((prev) =>
					prev.map((a) => (a.id === achievement.id ? { ...a, notified: true } : a))
				);
			}
		});
	}, [achievements, addNotification]);

	// Desbloqueia conquista secreta quando ultraMode é ativado
	useEffect(() => {
		if (ultraMode && achievements.length > 0) {
			const ultraAchievement = achievements.find((a) => a.id === "ultra_mode");
			if (ultraAchievement && !ultraAchievement.unlocked) {
				setAchievements((prev) =>
					prev.map((a) => (a.id === "ultra_mode" ? { ...a, unlocked: true } : a))
				);
				addNotification({
					message: "Secret Achievement Unlocked: ULTRA MODE! ⚡",
					type: "success",
					icon: "🎯",
				});
			}
		}
	}, [ultraMode, achievements, addNotification]);

	if (loading) {
		return (
			<div className='fixed top-4 right-4 z-50'>
				<div className='bg-black/30 backdrop-blur-sm rounded-xl p-4 border-2 border-white/20'>
					<div className='flex flex-col gap-3'>
						{[...Array(3)].map((_, i) => (
							<div
								key={i}
								className='w-12 h-12 bg-white/10 rounded-full animate-pulse'
							/>
						))}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='fixed top-4 right-4 z-50'>
			<div className='bg-black/30 backdrop-blur-sm rounded-xl p-4 border-2 border-white/20'>
				<div className='flex flex-col gap-3'>
					{achievements
						.filter((a) => !a.secret || (a.secret && ultraMode))
						.map((achievement) => (
							<div
								key={achievement.id}
								className='relative group'
								onMouseEnter={() => setHoveredAchievement(achievement.id)}
								onMouseLeave={() => setHoveredAchievement(null)}>
								<div
									className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl transition-all duration-300 ${
										achievement.unlocked
											? "bg-trump-yellow animate-starburst"
											: "bg-white/10"
									}`}>
									{achievement.icon}
								</div>

								{hoveredAchievement === achievement.id && (
									<div className='absolute left-full top-1/2 -translate-y-1/2 ml-4 whitespace-nowrap'>
										<div className='bg-black/90 text-white px-4 py-2 rounded-lg text-sm font-bold'>
											<div className='text-trump-yellow'>
												{achievement.title}
											</div>
											<div className='text-white/80 text-xs'>
												{achievement.description}
											</div>
										</div>
									</div>
								)}
							</div>
						))}
				</div>
			</div>
		</div>
	);
};

export default AchievementsPanel;
